import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, Grid, MenuItem, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { errorMessage, getClient, loadSfConfig, sfPath } from './sfApi';

// Field components live at module level (defining them inside the dialog would
// remount them on every keystroke and reset the cursor - see SolarRGF.js fix).
function SfText({ section, name, label, form, errors, onChange, required, type = 'text', inputProps, xs = 12, sm = 6 }) {
  const key = `${section}.${name}`;
  return (
    <Grid size={{ xs, sm }}>
      <TextField
        fullWidth size="small" label={label} required={required} type={type} inputProps={inputProps}
        value={form[section][name] ?? ''}
        onChange={(e) => onChange(section, name, e.target.value)}
        error={Boolean(errors[key])} helperText={errors[key] || ' '}
      />
    </Grid>
  );
}

function SfSelect({ section, name, label, options = [], form, errors, onChange, required, xs = 12, sm = 6 }) {
  const key = `${section}.${name}`;
  return (
    <Grid size={{ xs, sm }}>
      <TextField
        select fullWidth size="small" label={label} required={required}
        value={form[section][name] ?? ''}
        onChange={(e) => onChange(section, name, e.target.value)}
        error={Boolean(errors[key])} helperText={errors[key] || ' '}
      >
        {!required && <MenuItem value=""><em>None</em></MenuItem>}
        {options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </TextField>
    </Grid>
  );
}

function SfSwitch({ section, name, label, form, onChange }) {
  return (
    <Grid size={{ xs: 12, sm: 6 }}>
      <FormControlLabel
        control={<Switch checked={Boolean(form[section][name])} onChange={(e) => onChange(section, name, e.target.checked)} />}
        label={label}
      />
    </Grid>
  );
}

function SectionTitle({ children }) {
  return (
    <Grid size={12}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>{children}</Typography>
      <Divider sx={{ mb: 2 }} />
    </Grid>
  );
}

const EMPTY = { account: {}, contact: {}, opportunity: {} };
// sf-popup-fix-v1: drafts survive PreLeads re-renders/remounts
const draftCache = new Map();

/**
 * Props:
 *   open, onClose
 *   leadId           Mongo _id of the lead
 *   initialTarget    'account' | 'opportunity'
 *   apiClient        the app's axios instance (optional)
 *   onPushed(result) called after a successful push
 */
export default function SalesforcePushDialog({ open, onClose, leadId, initialTarget = 'account', apiClient, onPushed }) {
  const client = useMemo(() => getClient(apiClient), [apiClient]);
  const [config, setConfig] = useState(null);
  const [target, setTarget] = useState(initialTarget);
  const [form, setForm] = useState(EMPTY);
  const [known, setKnown] = useState({});
  const [lastPush, setLastPush] = useState(null);
  const [sameMailing, setSameMailing] = useState(true);
  const [sameWhatsapp, setSameWhatsapp] = useState(true);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [result, setResult] = useState(null);
  const [showPayload, setShowPayload] = useState(false);

  useEffect(() => { if (open) setTarget(initialTarget); }, [open, initialTarget]);

  useEffect(() => {
    if (!open || !leadId) return;
    let cancelled = false;
    setLoading(true); setAlert(null); setErrors({}); setResult(null); setShowPayload(false);
    Promise.all([loadSfConfig(apiClient), client.get(sfPath(apiClient, `/prefill/${leadId}`))])
      .then(([cfg, r]) => {
        if (cancelled) return;
        setConfig(cfg);
        const d = r.data;
        setForm(draftCache.get(String(leadId)) || { account: d.account, contact: d.contact, opportunity: d.opportunity });
        setKnown(d.known || {});
        setLastPush(d.lastPush);
        setSameWhatsapp(!d.contact.whatsapp || d.contact.whatsapp === d.contact.mobile);
      })
      .catch((err) => !cancelled && setAlert({ severity: 'error', text: errorMessage(err) }))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, leadId, apiClient, client]);

  const P = (config && config.picklists) || {};

  const onChange = (section, name, value) => {
    setForm((f) => { const n = { ...f, [section]: { ...f[section], [name]: value } }; draftCache.set(String(leadId), n); return n; });
    setErrors((e) => { const n = { ...e }; delete n[`${section}.${name}`]; return n; });
  };

  const buildBody = () => {
    const account = { ...form.account };
    const contact = { ...form.contact };
    if (sameMailing) {
      account.mailingStreet = account.billingStreet;
      account.mailingCity = account.billingCity;
      account.mailingState = account.billingState;
      account.mailingPostalCode = account.billingPostalCode;
      account.mailingCountry = account.billingCountry;
    }
    if (sameWhatsapp) contact.whatsapp = contact.mobile;
    // Keep Account name in sync with the person's name if left blank
    if (!account.name) account.name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
    const body = { target, account, contact };
    if (target === 'opportunity') body.opportunity = { ...form.opportunity };
    return body;
  };

  const submit = async () => {
    setSubmitting(true); setAlert(null); setResult(null);
    try {
      const r = await client.post(sfPath(apiClient, `/push/${leadId}`), buildBody());
      setResult(r.data);
      setKnown((k) => ({
        accountId: r.data.ids.accountId || k.accountId,
        contactId: r.data.ids.contactId || k.contactId,
        opportunityId: r.data.ids.opportunityId || k.opportunityId,
      }));
      const what = target === 'opportunity' ? 'Opportunity' : 'Account';
      setAlert(r.data.dryRun
        ? { severity: 'info', text: `Dry run: ${what} payload built and logged. Nothing was sent to Salesforce.` }
        : { severity: 'success', text: `${what} ${r.data.updatedExisting ? 'updated' : 'created'} in Salesforce.` });
      if (onPushed) onPushed(r.data);
    } catch (err) {
      const data = err.response && err.response.data;
      if (data && data.errors) setErrors(data.errors);
      setAlert({ severity: 'error', text: errorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  const fp = { form, errors, onChange };
  const isOpp = target === 'opportunity';
  const existsNote = isOpp
    ? (known.opportunityId ? `Opportunity already in Salesforce (${known.opportunityId}) - this will update it.` : (known.accountId ? 'Account exists - a new Opportunity will be linked to it.' : 'Account + Contact will be created first, then the Opportunity.'))
    : (known.accountId ? `Account already in Salesforce (${known.accountId}) - this will update it.` : 'A new Account and Contact will be created.');

  return (
    <Dialog open={open} onClose={(e, reason) => { if (submitting || reason === 'backdropClick') return; onClose(); }} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        Push to Salesforce
        {config && config.mode === 'dryrun' && <Chip size="small" color="warning" label="DRY RUN" />}
        <Box sx={{ flexGrow: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={target} onChange={(_, v) => v && setTarget(v)} disabled={submitting}>
          <ToggleButton value="account">Account</ToggleButton>
          <ToggleButton value="opportunity">Opportunity</ToggleButton>
        </ToggleButtonGroup>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            {alert && <Alert severity={alert.severity} sx={{ mb: 2 }}>{alert.text}</Alert>}
            {!alert && <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>{existsNote}</Alert>}
            {lastPush && lastPush.status === 'failed' && !result && (
              <Alert severity="warning" sx={{ mb: 2 }}>Last push failed: {lastPush.error}</Alert>
            )}

            <Grid container columnSpacing={2}>
              <SectionTitle>Customer</SectionTitle>
              <SfText section="contact" name="firstName" label="First name" {...fp} />
              <SfText section="contact" name="lastName" label="Last name" required {...fp} />
              <SfText section="account" name="name" label="Account name" required {...fp} />
              <SfText section="contact" name="email" label="Email" type="email" {...fp} />
              <SfText section="contact" name="mobile" label="Mobile" required inputProps={{ inputMode: 'numeric', maxLength: 10 }} {...fp} />
              {sameWhatsapp ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel control={<Checkbox checked onChange={() => setSameWhatsapp(false)} />} label="WhatsApp same as mobile" />
                </Grid>
              ) : (
                <SfText section="contact" name="whatsapp" label="WhatsApp number" inputProps={{ inputMode: 'numeric', maxLength: 10 }} {...fp} />
              )}
              <SfText section="account" name="consumerNumber" label="Consumer number (K No.)" {...fp} />

              <SectionTitle>Billing address</SectionTitle>
              <SfText section="account" name="billingStreet" label="Street" required xs={12} sm={12} {...fp} />
              <SfText section="account" name="billingCity" label="City" required sm={4} {...fp} />
              <SfSelect section="account" name="billingState" label="State" options={P.state} required sm={4} {...fp} />
              <SfText section="account" name="billingPostalCode" label="PIN code" required sm={4} inputProps={{ inputMode: 'numeric', maxLength: 6 }} {...fp} />
              <Grid size={12}>
                <FormControlLabel control={<Checkbox checked={sameMailing} onChange={(e) => setSameMailing(e.target.checked)} />} label="Mailing address same as billing" />
              </Grid>
              {!sameMailing && (
                <>
                  <SfText section="account" name="mailingStreet" label="Mailing street" xs={12} sm={12} {...fp} />
                  <SfText section="account" name="mailingCity" label="Mailing city" sm={4} {...fp} />
                  <SfSelect section="account" name="mailingState" label="Mailing state" options={P.state} sm={4} {...fp} />
                  <SfText section="account" name="mailingPostalCode" label="Mailing PIN" sm={4} {...fp} />
                </>
              )}

              <SectionTitle>KYC & finance</SectionTitle>
              <SfSelect section="account" name="customerStatus" label="Customer status" options={P.customerStatus} {...fp} />
              <SfSelect section="account" name="paymentMode" label="Preferred payment mode" options={P.paymentMode} {...fp} />
              <SfText section="account" name="cibilScore" label="CIBIL score" type="number" inputProps={{ min: 300, max: 900 }} sm={4} {...fp} />
              <SfText section="account" name="siteLatitude" label="Site latitude" type="number" sm={4} {...fp} />
              <SfText section="account" name="siteLongitude" label="Site longitude" type="number" sm={4} {...fp} />
              <SfSwitch section="account" name="aadharUploaded" label="Aadhaar uploaded" {...fp} />
              <SfSwitch section="account" name="panUploaded" label="PAN uploaded" {...fp} />
              <SfSwitch section="account" name="electricityBillUploaded" label="Electricity bill uploaded" {...fp} />
              <SfSwitch section="account" name="landDocUploaded" label="Land ownership doc uploaded (optional)" {...fp} />

              {isOpp && (
                <>
                  <SectionTitle>Opportunity - site & system</SectionTitle>
                  <SfText section="opportunity" name="name" label="Opportunity name" required xs={12} sm={12} {...fp} />
                  <SfSelect section="opportunity" name="leadSource" label="Lead source" options={P.leadSource} required {...fp} />
                  <SfSelect section="opportunity" name="typeOfConnection" label="Type of connection" options={P.typeOfConnection} {...fp} />
                  <SfSelect section="opportunity" name="systemType" label="System type" options={P.systemType} required sm={4} {...fp} />
                  <SfSelect section="opportunity" name="roofType" label="Roof type" options={P.roofType} required sm={4} {...fp} />
                  <SfSelect section="opportunity" name="floors" label="Number of floors" options={P.floors} required sm={4} {...fp} />
                  <SfText section="opportunity" name="capacityKw" label="Recommended capacity (kW)" type="number" required inputProps={{ min: 0.5, step: 0.5 }} {...fp} />
                  <SfText section="opportunity" name="roofAreaSqft" label="Roof area (sq ft)" type="number" inputProps={{ min: 0 }} {...fp} />
                </>
              )}
            </Grid>

            {result && result.payload && (
              <Box sx={{ mt: 1 }}>
                <Button size="small" onClick={() => setShowPayload((s) => !s)}>{showPayload ? 'Hide' : 'Show'} Salesforce payload</Button>
                <Collapse in={showPayload}>
                  <Box component="pre" sx={{ fontSize: 12, bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflow: 'auto', maxHeight: 300 }}>
                    {JSON.stringify(result.payload, null, 2)}
                  </Box>
                </Collapse>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>{result ? 'Close' : 'Cancel'}</Button>
        <Button
          variant="contained" onClick={submit}
          disabled={loading || submitting || !config || !config.enabled || !config.canPush}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitting ? 'Pushing...' : `Push ${isOpp ? 'Opportunity' : 'Account'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
